/**
 * eSIM relocation notices (#375) for the inventory page.
 *
 * - A group's `relocationHint` is written by QueueService when its members are
 *   moved between modems more than a few times in one UTC day. The server never
 *   clears it, so the page decides staleness from `hint.day`.
 * - A profile's `needsAttention` is written when a move deleted a limited
 *   profile and the download on the new modem failed. The next successful
 *   download clears it server-side.
 */
import type { ESimGroupRelocationHint, ESimProfileNeedsAttention } from '@simcheck/shared-types';

/** How many UTC days back a hint still shows: today plus the day before, so a
 *  hint set late in the UTC day does not vanish a few minutes later. */
export const RELOCATION_HINT_RECENT_DAYS = 1;

const utcDay = (d: Date) => d.toISOString().slice(0, 10);

/** The hint, when it is from today or the previous UTC day; else null. */
export function recentRelocationHint(
  hint: ESimGroupRelocationHint | null | undefined,
  now: Date = new Date(),
): ESimGroupRelocationHint | null {
  if (!hint || typeof hint.day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(hint.day)) return null;
  const oldest = utcDay(new Date(now.getTime() - RELOCATION_HINT_RECENT_DAYS * 86_400_000));
  // ISO dates compare correctly as strings. A future day (clock skew) still counts.
  return hint.day >= oldest ? hint : null;
}

/** True when the hint's day is the current UTC day. */
export function isRelocationHintToday(hint: ESimGroupRelocationHint, now: Date = new Date()): boolean {
  return hint.day === utcDay(now);
}

/** The modem IMEI the group's last move went to. The hint carries it only in
 *  its message ("last move went to modem <imei>"); null when absent. */
export function relocationHintTargetImei(hint: ESimGroupRelocationHint): string | null {
  const m = /last move went to modem ([0-9A-Za-z:_-]+)/.exec(hint.message || '');
  return m ? m[1] : null;
}

/** The profile's needs-attention flag, when it is set and has something to say. */
export function profileNeedsAttention(
  value: ESimProfileNeedsAttention | null | undefined,
): ESimProfileNeedsAttention | null {
  if (!value || typeof value !== 'object') return null;
  if (!value.reason && !value.message) return null;
  return value;
}

/** Firestore Timestamp, Date, ISO string or millis to a Date; null otherwise. */
export function noticeDate(at: unknown): Date | null {
  if (!at) return null;
  if (at instanceof Date) return Number.isNaN(at.getTime()) ? null : at;
  const ts = at as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof ts.toDate === 'function') return ts.toDate();
  const secs = typeof ts.seconds === 'number' ? ts.seconds : ts._seconds;
  if (typeof secs === 'number') return new Date(secs * 1000);
  if (typeof at === 'string' || typeof at === 'number') {
    const d = new Date(at);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}
